/* eslint-disable react/no-unescaped-entities, react-hooks/immutability, react-hooks/set-state-in-effect */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import api, { usersAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { MandateTimelineCompact } from '../../components/mandates/MandateTimeline';
import { DirigeantCockpit } from '../../components/dashboard/DirigeantCockpit';
import { PulseLiveFeed } from '../../components/dashboard/PulseLiveFeed';
import AIPrioritiesPanel from '../../components/dashboard/AIPrioritiesPanel';
import AIChatModal from '../../components/ai/AIChatModal';
import TeamDataQualityWidget from '../../components/dashboard/TeamDataQualityWidget';
import DirectorMissionCard from '../../components/dashboard/DirectorMissionCard';
import DailyInsightHero from '../../components/dashboard/DailyInsightHero';
import WeakSignalsPanel from '../../components/dashboard/WeakSignalsPanel';
import KPIBouquetsStrip from '../../components/cockpit/KPIBouquetsStrip';
import WeeklyRecapBanner from '../../components/cockpit/WeeklyRecapBanner';
import AIWeeklySynthesisV2 from '../../components/dashboard/AIWeeklySynthesisV2';
import VisitsWidget from '../../components/dashboard/VisitsWidget';
import CompromisWidget from '../../components/dashboard/CompromisWidget';
import ActivityWidget from '../../components/dashboard/ActivityWidget';
import OverpricedMandatesWidget from '../../components/dvf/OverpricedMandatesWidget';
import WelcomeChecklist from '../../components/dashboard/WelcomeChecklist';
import { PageHeader, EmptyState, HomlyButton } from '../../components/homly';
import {
    FileText,
    Users,
    ArrowRight,
    PenTool,
    Upload,
    Clock,
    CheckCircle,
    Sparkles,
    Send,
    BarChart3,
    Settings,
    LayoutDashboard,
    UsersRound,
    Brain,
    FolderClosed,
    Euro
} from 'lucide-react';

// Status configuration
const STATUS_CONFIG = {
    draft: { label: 'Brouillon', color: 'bg-slate-500/20 text-slate-400', icon: Clock },
    generated: { label: 'PDF généré', color: 'bg-blue-500/20 text-blue-400', icon: FileText },
    sent: { label: 'Envoyé', color: 'bg-amber-500/20 text-amber-400', icon: Send },
    acknowledged: { label: 'Validé', color: 'bg-green-500/20 text-green-400', icon: CheckCircle }
};

// Animation variants
const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
};

const stagger = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.1 } }
};

function AdminDashboard() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [mandates, setMandates] = useState([]);
    const [team, setTeam] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(() => {
        try {
            return localStorage.getItem('homly_admin_dashboard_tab') || 'overview';
        } catch {
            return 'overview';
        }
    });
    const [aiChatOpen, setAiChatOpen] = useState(false);

    const handleTabChange = (val) => {
        setActiveTab(val);
        try { localStorage.setItem('homly_admin_dashboard_tab', val); } catch { /* quota */ }
    };

    // Deep-link : permet d'arriver directement sur un onglet via /admin?tab=deep-analysis
    // (utilisé par la carte « À traiter aujourd'hui » de la sidebar).
    // Note : 'actions' et 'ask-ai' sont conservés en redirect doux pour compat liens externes.
    const [searchParams] = useSearchParams();
    useEffect(() => {
        const t = searchParams.get('tab');
        if (!t) return;
        if (t === 'ask-ai') {
            setAiChatOpen(true);
            return;
        }
        if (t === 'actions') {
            // ancien onglet supprimé → redirige vers cockpit du jour (où vivent les priorités)
            setActiveTab('overview');
            try { localStorage.setItem('homly_admin_dashboard_tab', 'overview'); } catch { /* quota */ }
            return;
        }
        if (['overview', 'deep-analysis', 'mandates', 'team'].includes(t)) {
            setActiveTab(t);
            try { localStorage.setItem('homly_admin_dashboard_tab', t); } catch { /* quota */ }
        }
    }, [searchParams]);

    useEffect(() => {
        loadDashboardData();
    }, []);

    /**
     * Loading progressif : on lance les 3 requêtes en parallèle MAIS on rend l'UI
     * dès que les mandates arrivent (rapides). Les stats lents (revenue, closings) sont
     * hydratés ensuite — l'utilisateur voit le contenu tout de suite au lieu d'attendre 1.5s.
     */
    const loadDashboardData = async () => {
        // 1. Mandates + team : on attend ces 2-là (rapides ~500ms) pour afficher le dashboard
        try {
            const [mandatesRes, teamRes] = await Promise.all([
                api.get('/mandates-v2/'),
                usersAPI.getTeam(),
            ]);
            const allMandates = mandatesRes.data.mandates || [];
            const calculatedStats = {
                active_mandates: allMandates.filter(m => ['draft', 'generated', 'sent', 'actif', 'active', 'acknowledged'].includes(m.status)).length,
                signed_mandates: allMandates.filter(m => ['acknowledged', 'active', 'actif'].includes(m.status)).length,
                sold_mandates: allMandates.filter(m => ['vendu', 'sold'].includes(m.status)).length,
                total_mandates: allMandates.length,
                total_revenue: 0,        // hydraté ensuite
                closings_this_month: 0,  // hydraté ensuite
            };
            setStats(calculatedStats);
            setMandates(allMandates.slice(0, 5));
            setTeam(teamRes.data);
            setLoading(false);  // 🎯 Le dashboard est visible dès maintenant
        } catch (err) {
            console.error('Dashboard error:', err);
            toast.error('Erreur lors du chargement des données');
            setLoading(false);
            return;
        }

        // 2. Stats agency (lent ~1s) : hydratation asynchrone sans bloquer l'UI
        try {
            const agencyStatsRes = await api.get('/admin/agency/stats?period=month');
            const agencyStats = agencyStatsRes.data || {};
            setStats((prev) => prev ? {
                ...prev,
                total_revenue: agencyStats.total_revenue || 0,
                closings_this_month: agencyStats.closings_this_month || 0,
            } : prev);
        } catch (_e) {
            // Stats facultatifs — on ne bloque pas l'UI si ça échoue
        }
    };

    const basePath = user?.role === 'admin' ? '/admin' : '/agent';

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[...Array(4)].map((_, i) => (
                        <div key={i} className="bg-card rounded-2xl p-6 border border-border">
                            <Skeleton className="h-16 w-full bg-muted" />
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <motion.div 
            className="space-y-8" 
            data-testid="admin-dashboard"
            initial="hidden"
            animate="visible"
            variants={stagger}
        >
            {/* Header — Cockpit du jour (Dark Luxury) */}
            <motion.div variants={fadeIn}>
                <PageHeader
                    kicker={`Bonjour ${user?.first_name || ''}, voici votre cockpit du jour`}
                    title={
                        stats && stats.active_mandates > 0
                            ? `${stats.active_mandates} mandat${stats.active_mandates > 1 ? 's' : ''} actif${stats.active_mandates > 1 ? 's' : ''}${stats.closings_this_month > 0 ? ` · ${stats.closings_this_month} vente${stats.closings_this_month > 1 ? 's' : ''} ce mois` : ''}`
                            : 'Votre cockpit IA Homly'
                    }
                    subtitle={
                        stats && stats.active_mandates > 0
                            ? `${stats.signed_mandates > 0 ? `${stats.signed_mandates} signé${stats.signed_mandates > 1 ? 's' : ''} · ` : ''}${team.length > 1 ? `${team.length} agents mobilisés · ` : ''}Les urgences IA sont juste en dessous.`
                            : stats && stats.total_mandates > 0
                                ? `${stats.total_mandates} mandat${stats.total_mandates > 1 ? 's' : ''} en préparation. Finalisez-les pour activer le suivi IA.`
                                : `Créez votre premier mandat pour activer l'assistant IA de votre agence.`
                    }
                    primaryAction={{
                        label: 'Uploader un mandat',
                        icon: Upload,
                        onClick: () => navigate('/admin/mandates/upload'),
                    }}
                    secondaryAction={{
                        label: 'Demander à l\'IA',
                        icon: Sparkles,
                        onClick: () => setAiChatOpen(true),
                    }}
                    dataTestid="admin-cockpit-header"
                />
            </motion.div>

            {/* Welcome Checklist (auto-masquée si terminée ou fermée) */}
            <motion.div variants={fadeIn}>
                <WelcomeChecklist stats={stats || {}} team={team} basePath="/admin" />
            </motion.div>

            {/* Empty State or KPI Cards + Content */}
            {stats?.total_mandates === 0 && mandates.length === 0 ? (
                <motion.div variants={fadeIn} data-testid="admin-empty-state">
                    <div
                        style={{
                            background: 'var(--homly-bg-elevated)',
                            border: '1px solid var(--homly-purple-border)',
                            borderRadius: 'var(--homly-radius-xl)',
                            boxShadow: 'var(--homly-shadow-glow-purple)',
                        }}
                    >
                        <EmptyState
                            icon={FileText}
                            title="Bienvenue sur Homly ! Référençons votre premier mandat."
                            description="Uploadez le PDF ou photographiez votre mandat papier signé — l'IA extrait les infos en quelques secondes. Ensuite, l'IA analyse votre activité pour vous proposer chaque jour 3 actions concrètes : qui appeler, qui relancer, qui rassurer."
                            primaryAction={{
                                label: 'Uploader un mandat',
                                icon: Upload,
                                onClick: () => navigate('/admin/mandates/upload'),
                            }}
                            accent="purple"
                        />
                    </div>
                </motion.div>
            ) : (
            <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full" data-testid="admin-dashboard-tabs">
                {/* Sticky horizontal tabs (style Metrion) — toujours visible */}
                <div className="sticky top-0 z-30 -mx-3 sm:-mx-6 px-3 sm:px-6 pb-3 pt-1 bg-background/85 backdrop-blur-md border-b border-border/40">
                    <TabsList className="bg-card border border-border h-auto p-1 inline-flex gap-1 w-auto max-w-full overflow-x-auto scrollbar-hide">
                        <TabsTrigger value="overview" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#681793] data-[state=active]:to-[#FF914D] data-[state=active]:text-white text-xs sm:text-sm px-3 sm:px-4 h-8 gap-1.5" data-testid="tab-overview">
                            <LayoutDashboard className="w-3.5 h-3.5" />
                            <span>Cockpit du jour</span>
                        </TabsTrigger>
                        <TabsTrigger value="deep-analysis" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#681793] data-[state=active]:to-[#FF914D] data-[state=active]:text-white text-xs sm:text-sm px-3 sm:px-4 h-8 gap-1.5" data-testid="tab-deep-analysis">
                            <Brain className="w-3.5 h-3.5" />
                            <span>Analyse approfondie</span>
                        </TabsTrigger>
                        <TabsTrigger value="mandates" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#681793] data-[state=active]:to-[#FF914D] data-[state=active]:text-white text-xs sm:text-sm px-3 sm:px-4 h-8 gap-1.5" data-testid="tab-mandates">
                            <FileText className="w-3.5 h-3.5" />
                            <span>Activité</span>
                        </TabsTrigger>
                        <TabsTrigger value="team" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#681793] data-[state=active]:to-[#FF914D] data-[state=active]:text-white text-xs sm:text-sm px-3 sm:px-4 h-8 gap-1.5" data-testid="tab-team">
                            <UsersRound className="w-3.5 h-3.5" />
                            <span>Équipe</span>
                        </TabsTrigger>
                    </TabsList>
                </div>

                {/* TAB 1 — Cockpit du jour : "Que faire aujourd'hui ?" */}
                <TabsContent value="overview" className="mt-4 space-y-4 sm:space-y-5">
                    {/* Encart hebdomadaire chiffré — P5 */}
                    <WeeklyRecapBanner scope="agency" />
                    {/* Vue synthétique — 5 bouquets KPI agence (P3) */}
                    <motion.div variants={fadeIn} initial="hidden" animate="visible"
                                data-testid="admin-bouquets-strip">
                        <KPIBouquetsStrip scope="agency" variant="admin" />
                    </motion.div>
                    {/* Mission Providence assignée par le PDG du groupe — première chose visible si présente */}
                    <motion.div variants={fadeIn} initial="hidden" animate="visible">
                        <DirectorMissionCard />
                    </motion.div>
                    {/* Qualité de saisie équipe — n'apparaît que si gaps réels (cf. composant) */}
                    <motion.div variants={fadeIn} initial="hidden" animate="visible">
                        <TeamDataQualityWidget />
                    </motion.div>
                    {/* 1. Urgences IA — première chose que le manager voit */}
                    <motion.div variants={fadeIn} initial="hidden" animate="visible">
                        <AIPrioritiesPanel basePath="/admin" />
                    </motion.div>
                    {/* 2. Daily Insight (Claude Sonnet 4.5, cache 24h) — le pourquoi du jour */}
                    <motion.div variants={fadeIn} initial="hidden" animate="visible">
                        <DailyInsightHero />
                    </motion.div>
                    {/* 3. Cockpit Dirigeant — KPIs agence (sans la synthèse hebdo, déplacée vers Analyse approfondie) */}
                    <motion.div variants={fadeIn} initial="hidden" animate="visible">
                        <DirigeantCockpit mandates={mandates} team={team} stats={stats} showAIWeekly={false} />
                    </motion.div>
                </TabsContent>

                {/* TAB 2 — Analyse approfondie : "Pourquoi ? Tendances et signaux" */}
                <TabsContent value="deep-analysis" className="mt-4 space-y-4 sm:space-y-5" data-testid="deep-analysis-content">
                    {/* Synthèse Pilote hebdo (Claude) */}
                    <motion.div variants={fadeIn} initial="hidden" animate="visible">
                        <AIWeeklySynthesisV2 />
                    </motion.div>
                    {/* Signaux faibles hebdo — patterns invisibles */}
                    <motion.div variants={fadeIn} initial="hidden" animate="visible">
                        <WeakSignalsPanel />
                    </motion.div>
                    {/* Mandats surévalués (DVF) */}
                    <motion.div variants={fadeIn} initial="hidden" animate="visible">
                        <OverpricedMandatesWidget />
                    </motion.div>
                </TabsContent>

                {/* TAB 3 — Activité (Derniers mandats + Pulse Live Feed) */}
                <TabsContent value="mandates" className="mt-4">
                    <div className="grid lg:grid-cols-3 gap-4 sm:gap-6">
                        <motion.div variants={fadeIn} initial="hidden" animate="visible" className="lg:col-span-2">
                            <div className="bg-card backdrop-blur-sm rounded-xl sm:rounded-2xl border border-border overflow-hidden">
                                <div className="flex items-center justify-between p-3 sm:p-6 border-b border-border">
                                    <h2 className="text-sm sm:text-lg font-bold text-foreground flex items-center gap-2">
                                        <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-[#681793]" />
                                        Derniers mandats
                                    </h2>
                                    <Link to="/admin/mandates">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="text-muted-foreground hover:text-white hover:bg-card text-xs h-7 px-2"
                                            data-testid="view-all-mandates-button"
                                        >
                                            Voir tout
                                            <ArrowRight className="w-3 h-3 ml-1" />
                                        </Button>
                                    </Link>
                                </div>
                                <div className="p-3 sm:p-6">
                                    {mandates.length === 0 ? (
                                        <div className="text-center py-12">
                                            <div className="w-16 h-16 rounded-2xl bg-card flex items-center justify-center mx-auto mb-4">
                                                <FileText className="w-8 h-8 text-white/30" />
                                            </div>
                                            <p className="text-muted-foreground">Aucun mandat pour le moment</p>
                                            <Link to="/admin/mandates/upload">
                                                <Button className="mt-4 bg-gradient-to-r from-[#681793] to-[#FF914D] hover:opacity-90">
                                                    <Upload className="w-4 h-4 mr-2" />
                                                    Uploader un mandat
                                                </Button>
                                            </Link>
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            {mandates.slice(0, 6).map((mandate, idx) => {
                                                const statusConfig = STATUS_CONFIG[mandate.status] || STATUS_CONFIG.draft;
                                                const StatusIcon = statusConfig.icon;
                                                const salePrice = mandate.pricing?.sale_price || 0;
                                                const sellerNames = mandate.sellers?.map(s => `${s.first_name} ${s.last_name}`).join(', ') || '';
                                                const address = mandate.property?.address || mandate.address || '';

                                                return (
                                                    <motion.div
                                                        key={mandate.id}
                                                        initial={{ opacity: 0, x: -20 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        transition={{ delay: idx * 0.05 }}
                                                    >
                                                        <Link
                                                            to={`/admin/mandates/v2/${mandate.id}`}
                                                            className="block"
                                                        >
                                                            <div className="flex items-center justify-between p-3 sm:p-4 rounded-xl bg-card hover:bg-muted border border-border hover:border-border transition-all duration-200 group gap-2 sm:gap-4">
                                                                <div className="flex items-center gap-2.5 sm:gap-4 min-w-0 flex-1">
                                                                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-[#681793]/20 to-[#FF914D]/20 flex items-center justify-center group-hover:scale-105 transition-transform flex-shrink-0">
                                                                        <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-[#681793]" />
                                                                    </div>
                                                                    <div className="min-w-0 flex-1">
                                                                        <div className="flex items-center gap-2">
                                                                            <p className="font-semibold text-foreground text-sm truncate">
                                                                                {mandate.mandate_number}
                                                                            </p>
                                                                            <MandateTimelineCompact status={mandate.status} />
                                                                        </div>
                                                                        <p className="text-xs text-muted-foreground truncate mt-0.5">
                                                                            {sellerNames}{address ? ` — ${address}` : ''}
                                                                        </p>
                                                                        {salePrice > 0 && (
                                                                            <p className="text-[10px] text-[#FF914D] font-medium mt-0.5">
                                                                                {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(salePrice)}
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <Badge className={`${statusConfig.color} border-0 flex items-center gap-1 flex-shrink-0 text-[10px] sm:text-xs px-2 py-0.5`}>
                                                                    <StatusIcon className="w-3 h-3" />
                                                                    <span className="hidden sm:inline">{statusConfig.label}</span>
                                                                </Badge>
                                                            </div>
                                                        </Link>
                                                    </motion.div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </motion.div>

                        {/* Pulse Live Feed - sidebar desktop, full width mobile */}
                        <motion.div variants={fadeIn} initial="hidden" animate="visible">
                            <PulseLiveFeed mandates={mandates} team={team} />
                        </motion.div>
                    </div>

                    {/* Sous-section : Activité commerciale, Visites, Pipeline Compromis */}
                    <div className="mt-4 sm:mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                        <motion.div variants={fadeIn} initial="hidden" animate="visible">
                            <ActivityWidget />
                        </motion.div>
                        <motion.div variants={fadeIn} initial="hidden" animate="visible">
                            <VisitsWidget />
                        </motion.div>
                        <motion.div variants={fadeIn} initial="hidden" animate="visible" className="lg:col-span-2">
                            <CompromisWidget />
                        </motion.div>
                    </div>
                </TabsContent>

                {/* TAB 4 — Equipe (team summary + acces rapides) */}
                <TabsContent value="team" className="mt-4">
                    <div className="grid lg:grid-cols-2 gap-4 sm:gap-6">
                        <motion.div variants={fadeIn} initial="hidden" animate="visible">
                            <div className="bg-card backdrop-blur-sm rounded-2xl border border-border overflow-hidden h-full">
                                <div className="flex items-center justify-between p-6 border-b border-border">
                                    <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
                                        <Users className="w-5 h-5 text-[#FF914D]" />
                                        Membres de l'équipe ({team.length})
                                    </h2>
                                    <Link to="/admin/team">
                                        <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-white hover:bg-card text-xs h-7 px-2">
                                            Gérer
                                            <ArrowRight className="w-3 h-3 ml-1" />
                                        </Button>
                                    </Link>
                                </div>
                                <div className="p-6">
                                    {team.length === 0 ? (
                                        <div className="text-center py-6">
                                            <p className="text-muted-foreground text-sm">Aucun membre</p>
                                            <Link to="/admin/team">
                                                <Button className="mt-3 bg-gradient-to-r from-[#681793] to-[#FF914D] hover:opacity-90 text-sm">
                                                    <Users className="w-4 h-4 mr-2" />
                                                    Inviter un agent
                                                </Button>
                                            </Link>
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            {team.map((member) => (
                                                <div key={member.id} className="flex items-center gap-3 p-3 rounded-xl bg-card hover:bg-muted transition-colors">
                                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#681793] to-[#FF914D] flex items-center justify-center text-white font-bold text-sm">
                                                        {member.first_name?.[0]}{member.last_name?.[0]}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium text-foreground truncate">
                                                            {member.first_name} {member.last_name}
                                                        </p>
                                                        <p className="text-xs text-muted-foreground capitalize">{member.role}</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </motion.div>

                        <motion.div variants={fadeIn} initial="hidden" animate="visible">
                            <div className="bg-gradient-to-br from-[#681793]/15 to-[#FF914D]/15 rounded-2xl p-6 border border-[#681793]/30 h-full">
                                <h3 className="font-bold text-foreground mb-4 flex items-center gap-2">
                                    <Settings className="w-4 h-4 text-[#FF914D]" />
                                    Accès rapides
                                </h3>
                                <div className="space-y-2">
                                    <Link to="/admin/agency">
                                        <Button variant="ghost" className="w-full justify-start text-muted-foreground hover:text-white hover:bg-muted">
                                            <BarChart3 className="w-4 h-4 mr-2" />
                                            KPIs Agence
                                        </Button>
                                    </Link>
                                    <Link to="/notaire/dossiers" data-testid="admin-nav-notary">
                                        <Button variant="ghost" className="w-full justify-start text-muted-foreground hover:text-white hover:bg-muted">
                                            <FolderClosed className="w-4 h-4 mr-2" />
                                            Dossiers notaire
                                        </Button>
                                    </Link>
                                    <Link to="/agence/commissions" data-testid="admin-nav-commissions">
                                        <Button variant="ghost" className="w-full justify-start text-muted-foreground hover:text-white hover:bg-muted">
                                            <Euro className="w-4 h-4 mr-2" />
                                            Commissions
                                        </Button>
                                    </Link>
                                    <Link to="/admin/kpi">
                                        <Button variant="ghost" className="w-full justify-start text-muted-foreground hover:text-white hover:bg-muted">
                                            <Sparkles className="w-4 h-4 mr-2" />
                                            Catalogue KPI
                                        </Button>
                                    </Link>
                                    <Link to="/admin/team">
                                        <Button variant="ghost" className="w-full justify-start text-muted-foreground hover:text-white hover:bg-muted">
                                            <Users className="w-4 h-4 mr-2" />
                                            Inviter un agent
                                        </Button>
                                    </Link>
                                    <Link to="/admin/settings">
                                        <Button variant="ghost" className="w-full justify-start text-muted-foreground hover:text-white hover:bg-muted">
                                            <Settings className="w-4 h-4 mr-2" />
                                            Paramètres
                                        </Button>
                                    </Link>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </TabsContent>
            </Tabs>
            )}
            {/* Chat IA centralisé — accessible depuis le header de tous les onglets */}
            <AIChatModal open={aiChatOpen} onClose={() => setAiChatOpen(false)} role="admin" />
            {/* GuidedTour & HelpButton are mounted globally in MainLayout — do not duplicate here */}
        </motion.div>
    );
}

export default AdminDashboard;
